/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { connect } from 'react-redux';
import { Feature } from 'ol';
import { getLength } from 'ol/sphere';
import LineString from 'ol/geom/LineString';
import MultiPoint from 'ol/geom/MultiPoint';
import Point from 'ol/geom/Point';
import PropTypes from 'prop-types';
import { ScaleLine } from 'ol/control';

import {
  addFeaturesToLayer,
  clearMapLayer,
  createLayer,
} from 'utils/map/layers';
import { addLayerToMap, addOverlayToMap } from 'utils/map/map';
import { createOverlay, removeOverlayIfExists } from 'utils/map/overlays';
import { hasObjectKeys } from 'utils/main';
import { showPopup } from 'components/uavos/utils/common';
import ThemeContext from 'components/wrapper/themeContext';
import {
  clearLabelPointsOnLine,
  DEFAULT_RULER_LABEL_STEP,
  getLabelPointStyle,
  getLayerFeatureById,
  getLineStyle,
  getMultiPointStyle,
  getRotation,
  removeLayerFeature,
  RULER_FEATURES_IDS,
  RULER_FEATURES_NAMES,
} from './utils/RulerUtils';
import { RulerPopup } from './Popup/RulerPopup';

const Ruler = ({ map }) => {
  const [clickEvent, setClickEvent] = useState({});
  const [clickedPoints, setClickedPoints] = useState([]);
  const [doubleClickEvent, setDoubleClickEvent] = useState({});
  const [zoomEvent, setZoomEvent] = useState({});
  const [scaleLength, setScaleLength] = useState();
  const [scaleLabel, setScaleLabel] = useState();
  const [pointerEvent, setPointerEvent] = useState({});
  const [rightClickEvent, setRightClickEvent] = useState(null);
  const [rulerWithPointerLength, setRulerWithPointerLength] = useState(0);
  const didUserDoubleClicked = useRef(true);
  const zoomLevel = useRef(null);
  const rulerLength = useRef(0);
  const lastInnerPointWithLabel = useRef();
  const isClickAvailable = useRef(true);
  const rulerLayer = useRef(null);
  const pointsLayer = useRef(null);
  const popup = useRef(null);
  const popupOverlay = useRef(null);
  const timer = useRef(null);
  const { themeType } = useContext(ThemeContext);

  const calculateLengthBasedOnScale = useCallback(
    initialRulerWithPointerLength => {
      if (scaleLabel === 'km')
        return getLength(initialRulerWithPointerLength.getGeometry()) / 1000;
      if (scaleLabel === 'mm')
        return getLength(initialRulerWithPointerLength.getGeometry()) * 1000;
      return getLength(initialRulerWithPointerLength.getGeometry());
    },
    [scaleLabel]
  );

  const calculateInnerPoints = useCallback(
    (startCoord, endCoord, initialOffsetLength = 0) => {
      const middlePoints = [];
      const rotation = getRotation(startCoord, endCoord);
      const tmpLineFeature = new Feature({
        geometry: new LineString([startCoord, endCoord]),
      });
      const length = calculateLengthBasedOnScale(tmpLineFeature);
      const numberOfSegments = Math.floor(length / scaleLength);
      const offset = initialOffsetLength / length;
      for (let i = 0; i <= numberOfSegments; i += 1) {
        if ((i * scaleLength) / length + offset <= 1) {
          const intersectCoordinate = tmpLineFeature
            .getGeometry()
            .getCoordinateAt((i * scaleLength) / length + offset);
          middlePoints.push(intersectCoordinate);
        }
      }
      return { length, middlePoints, rotation };
    },
    [calculateLengthBasedOnScale, scaleLength]
  );

  const displayInnerPointsAndGetLastOneWithLabel = useCallback(
    (
      innerPoints,
      rotation,
      onHover = false,
      lineLength = rulerLength.current
    ) => {
      let lastInnerPoint = null;
      const featureId = onHover
        ? RULER_FEATURES_IDS.HOVER_RULER_MULTI_POINTS
        : `${RULER_FEATURES_IDS.RULER_MULTI_POINTS}-${clickedPoints.length +
            1}`;
      const markers = new Feature({
        geometry: new MultiPoint(innerPoints),
      });
      markers.setStyle(getMultiPointStyle(rotation, themeType));
      markers.setId(featureId);
      markers.setProperties({
        name: onHover
          ? RULER_FEATURES_NAMES.HOVER_RULER_MULTI_POINTS
          : RULER_FEATURES_NAMES.RULER_MULTI_POINTS,
      });
      let pointsCounter = lineLength
        ? Math.floor(lineLength / scaleLength) + 1
        : 0;
      const labels = [];
      for (const innerPoint of innerPoints) {
        if (!(pointsCounter % DEFAULT_RULER_LABEL_STEP)) {
          const labelPoint = new Feature({
            geometry: new Point(innerPoint),
          });
          labelPoint.setStyle(
            getLabelPointStyle(
              themeType,
              `${pointsCounter * scaleLength} ${scaleLabel}`,
              rotation
            )
          );
          labelPoint.setProperties({
            name: onHover
              ? RULER_FEATURES_NAMES.HOVER_LABEL_POINT
              : RULER_FEATURES_NAMES.LABEL_POINT,
          });
          labels.push(labelPoint);
          lastInnerPoint = innerPoint;
        }
        pointsCounter += 1;
      }
      addFeaturesToLayer(pointsLayer.current, [markers, ...labels]);
      return lastInnerPoint;
    },
    [clickedPoints, scaleLabel, scaleLength, themeType]
  );

  const displayTotalInnerPointsAndGetLastOneWithLabel = useCallback(
    innerPointsSegments => {
      let pointsCounter = 0;
      let lastInnerPoint = null;
      for (const innerPointsSegment of innerPointsSegments) {
        const { rotation, points: innerPoints } = innerPointsSegment;
        const markers = new Feature({
          geometry: new MultiPoint(innerPoints),
        });
        markers.setStyle(getMultiPointStyle(rotation, themeType));
        const labels = [];
        for (const innerPoint of innerPoints) {
          if (!(pointsCounter % DEFAULT_RULER_LABEL_STEP)) {
            const labelPoint = new Feature({
              geometry: new Point(innerPoint),
            });
            labelPoint.setStyle(
              getLabelPointStyle(
                themeType,
                `${pointsCounter * scaleLength} ${scaleLabel}`,
                rotation
              )
            );
            labelPoint.setProperties({
              name: RULER_FEATURES_NAMES.LABEL_POINT,
            });
            labels.push(labelPoint);
            lastInnerPoint = innerPoint;
          }
          pointsCounter += 1;
        }
        addFeaturesToLayer(pointsLayer.current, [markers, ...labels]);
      }
      return lastInnerPoint;
    },
    [scaleLabel, scaleLength, themeType]
  );

  const addFinishingPointToLayer = useCallback(
    (
      totalLength = rulerLength.current,
      lastInnerPoint = lastInnerPointWithLabel.current
    ) => {
      if (clickedPoints.length < 2) return;
      const finishingPoint = new Feature({
        geometry: new Point(clickedPoints[clickedPoints.length - 1]),
      });
      const rotation = getRotation(
        clickedPoints[clickedPoints.length - 2],
        clickedPoints[clickedPoints.length - 1]
      );
      finishingPoint.setStyle(getMultiPointStyle(rotation, themeType));
      const finishingPointLabel = new Feature({
        geometry: new Point(clickedPoints[clickedPoints.length - 1]),
      });
      finishingPoint.setId(RULER_FEATURES_IDS.LAST_POINT_MARKER);
      finishingPointLabel.setId(RULER_FEATURES_IDS.LAST_POINT_LABEL);
      const length =
        scaleLabel === 'km' ? totalLength.toFixed(2) : totalLength.toFixed(0);
      let additionalXOffset = 0;
      let additionalYOffset = 0;
      const lastMiddlePointPosition = map.getPixelFromCoordinate(
        lastInnerPoint
      );
      const lastPointPosition = map.getPixelFromCoordinate(
        clickedPoints[clickedPoints.length - 1]
      );
      const popupHeight = 20;
      const popupWidth = 60;
      const xDiff = lastPointPosition[0] - lastMiddlePointPosition[0];
      const yDiff = lastPointPosition[1] - lastMiddlePointPosition[1];
      const xDiffAbs = Math.abs(xDiff);
      const yDiffAbs = Math.abs(yDiff);
      if (yDiffAbs < popupHeight && xDiffAbs < popupWidth) {
        additionalYOffset = ((popupHeight - yDiffAbs) * yDiffAbs) / yDiff;
        additionalXOffset = ((popupWidth - xDiffAbs) * xDiffAbs) / xDiff;
      }
      finishingPointLabel.setStyle(
        getLabelPointStyle(
          themeType,
          `${length} ${scaleLabel}`,
          rotation,
          additionalXOffset,
          additionalYOffset
        )
      );
      finishingPointLabel.setId(RULER_FEATURES_IDS.LAST_POINT_LABEL);
      addFeaturesToLayer(pointsLayer.current, [
        finishingPoint,
        finishingPointLabel,
      ]);
    },
    [clickedPoints, map, scaleLabel, themeType]
  );

  const displayPointerLineAndGetTotalLength = useCallback(
    lineLength => {
      if (!clickedPoints) return;
      let pointerFeature = getLayerFeatureById(
        pointsLayer.current,
        RULER_FEATURES_IDS.POINTER_LINE
      );
      if (!pointerFeature) {
        pointerFeature = new Feature({
          geometry: new LineString([
            clickedPoints[clickedPoints.length - 1],
            pointerEvent.coordinate,
          ]),
        });
        pointerFeature.setStyle(getLineStyle(themeType));
        pointerFeature.setId(RULER_FEATURES_IDS.POINTER_LINE);
        addFeaturesToLayer(pointsLayer.current, [pointerFeature]);
      } else if (pointsLayer.current.getSource()) {
        const multiPointsToRemove = pointsLayer.current
          .getSource()
          .getFeatureById(RULER_FEATURES_IDS.HOVER_RULER_MULTI_POINTS);
        if (multiPointsToRemove)
          pointsLayer.current.getSource().removeFeature(multiPointsToRemove);
        clearLabelPointsOnLine(pointsLayer);
      }
      pointerFeature.setStyle(getLineStyle(themeType));
      pointerFeature
        .getGeometry()
        .setCoordinates([
          clickedPoints[clickedPoints.length - 1],
          pointerEvent.coordinate,
        ]);
      const difference =
        lineLength % scaleLength ? scaleLength - (lineLength % scaleLength) : 0;
      const { middlePoints, rotation } = calculateInnerPoints(
        clickedPoints[clickedPoints.length - 1],
        pointerEvent.coordinate,
        difference
      );
      displayInnerPointsAndGetLastOneWithLabel(
        middlePoints,
        rotation,
        true,
        lineLength
      );
      const pointerLength = calculateLengthBasedOnScale(pointerFeature);
      const updatedRulerWithPointerLength =
        scaleLabel === 'km'
          ? (lineLength + pointerLength).toFixed(2)
          : (lineLength + pointerLength).toFixed(0);
      return updatedRulerWithPointerLength;
    },
    [
      calculateInnerPoints,
      calculateLengthBasedOnScale,
      displayInnerPointsAndGetLastOneWithLabel,
      clickedPoints,
      pointerEvent.coordinate,
      scaleLabel,
      scaleLength,
      themeType,
    ]
  );

  useEffect(() => {
    popupOverlay.current = createOverlay('popupRulerOverlay', popup.current);
    popupOverlay.current.setPosition(undefined);
    if (popup?.current) popup.current.blur();
    return () => {
      if (timer && timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!hasObjectKeys(map)) return;

    const handleSetContextMenu = evt => {
      evt.preventDefault();
      setRightClickEvent(evt);
    };

    const handleClick = evt => {
      evt.preventDefault();
      setClickEvent(evt);
    };

    const cleanup = () => {
      clearMapLayer(rulerLayer.current);
      clearMapLayer(pointsLayer.current);
      removeOverlayIfExists(map, popupOverlay.current);
      map.removeLayer(rulerLayer.current);
      map.removeLayer(pointsLayer.current);
      map.un('click', handleClick);
      map.un('dblclick', setDoubleClickEvent);
      map.un('moveend', setZoomEvent);
      map.un('pointermove', setPointerEvent);
      map.un('contextmenu', handleSetContextMenu);
    };

    rulerLayer.current = createLayer('rulerLayer');
    pointsLayer.current = createLayer('pointsLayer');
    addLayerToMap(map, rulerLayer.current);
    addLayerToMap(map, pointsLayer.current);
    addOverlayToMap(map, popupOverlay.current);
    map.on('click', handleClick);
    map.on('dblclick', setDoubleClickEvent);
    map.on('moveend', setZoomEvent);
    map.on('pointermove', setPointerEvent);
    map.on('contextmenu', handleSetContextMenu);

    return cleanup;
  }, [map]);

  useEffect(() => {
    if (!didUserDoubleClicked.current) {
      addFinishingPointToLayer();
      setRulerWithPointerLength(0);
      popupOverlay.current.setPosition(undefined);
      if (popup?.current) popup.current.blur();
    }
    didUserDoubleClicked.current = true;
  }, [doubleClickEvent]);

  useEffect(() => {
    if (!isClickAvailable.current) return;
    if (pointsLayer.current.getSource()) {
      clearLabelPointsOnLine(pointsLayer);
    }
    if (!didUserDoubleClicked.current) return;
    setClickedPoints([]);
    didUserDoubleClicked.current = false;
    setRulerWithPointerLength(0);
    rulerLength.current = 0;
    isClickAvailable.current = true;
    lastInnerPointWithLabel.current = null;

    clearMapLayer(rulerLayer.current);
    clearMapLayer(pointsLayer.current);
    popupOverlay.current.setPosition(undefined);
    if (popup?.current) popup.current.blur();
  }, [clickEvent]);

  useEffect(() => {
    if (clickEvent.coordinate && isClickAvailable.current) {
      setClickedPoints(prev => [...prev, clickEvent.coordinate]);
      isClickAvailable.current = false;
      timer.current = setTimeout(() => {
        isClickAvailable.current = true;
      }, 200);
    }
  }, [clickEvent]);

  useEffect(() => {
    if (clickedPoints.length < 2) return;

    const drawNewLineSegmentAndGetItsLength = () => {
      let lineFeature;
      let difference = 0;
      if (clickedPoints.length === 2) {
        lineFeature = new Feature({
          geometry: new LineString(clickedPoints),
        });
        lineFeature.setStyle(getLineStyle(themeType));
        lineFeature.setId(RULER_FEATURES_IDS.RULER_LINE);
        addFeaturesToLayer(rulerLayer.current, [lineFeature]);
      } else {
        lineFeature = getLayerFeatureById(
          rulerLayer.current,
          RULER_FEATURES_IDS.RULER_LINE
        );
        lineFeature
          .getGeometry()
          .appendCoordinate(clickedPoints[clickedPoints.length - 1]);
        difference =
          rulerLength.current % scaleLength
            ? scaleLength - (rulerLength.current % scaleLength)
            : 0;
      }
      const { length, middlePoints, rotation } = calculateInnerPoints(
        clickedPoints[clickedPoints.length - 2],
        clickedPoints[clickedPoints.length - 1],
        difference
      );
      const middlePointToSet = displayInnerPointsAndGetLastOneWithLabel(
        middlePoints,
        rotation
      );
      return [length, middlePointToSet];
    };

    const [
      newLineSegmentLength,
      lastMiddlePoint,
    ] = drawNewLineSegmentAndGetItsLength();
    rulerLength.current += newLineSegmentLength;
    if (lastMiddlePoint) {
      lastInnerPointWithLabel.current = lastMiddlePoint;
    }
  }, [clickedPoints]);

  useEffect(() => {
    if (map.getView().getZoom() !== zoomLevel.current) {
      map.getControls().forEach(control => {
        if (control instanceof ScaleLine) {
          const [value, label] = control?.renderedHTML_.split(' ');
          setScaleLabel(label);
          setScaleLength(Number(value));
        }
      });
    }
    zoomLevel.current = map.getView().getZoom();
  }, [zoomEvent]);

  useEffect(() => {
    if (!clickedPoints.length) return;
    if (clickedPoints.length === 1 && !didUserDoubleClicked.current) {
      const totalLength = displayPointerLineAndGetTotalLength(0);
      showPopup(popupOverlay.current, pointerEvent.coordinate);
      setRulerWithPointerLength(Number(totalLength));
      return;
    }
    const redrawRulerAndGetItsLength = () => {
      clearMapLayer(pointsLayer.current);
      let totalLength = 0;
      const totalMiddlePoints = [];
      const lineFeature = getLayerFeatureById(
        rulerLayer.current,
        RULER_FEATURES_IDS.RULER_LINE
      );
      lineFeature.setStyle(getLineStyle(themeType));
      lineFeature.getGeometry().forEachSegment((startCoord, endCoord) => {
        const difference =
          totalLength % scaleLength
            ? scaleLength - (totalLength % scaleLength)
            : 0;
        const { length, middlePoints, rotation } = calculateInnerPoints(
          startCoord,
          endCoord,
          difference
        );
        totalLength += length;
        totalMiddlePoints.push({ points: middlePoints, rotation });
      });
      for (const point of totalMiddlePoints) {
        const multiFeatureSaved = new Feature({
          geometry: new MultiPoint(point.points),
        });
        multiFeatureSaved.setStyle(
          getMultiPointStyle(point.rotation, themeType)
        );
        addFeaturesToLayer(pointsLayer.current, [multiFeatureSaved]);
      }
      const lastInnerPoint = displayTotalInnerPointsAndGetLastOneWithLabel(
        totalMiddlePoints
      );
      if (didUserDoubleClicked.current)
        addFinishingPointToLayer(totalLength, lastInnerPoint);
      return [totalLength, lastInnerPoint];
    };

    const [updatedLength, innerPoint] = redrawRulerAndGetItsLength();
    if (!didUserDoubleClicked.current) {
      const totalLength = displayPointerLineAndGetTotalLength(updatedLength);
      showPopup(popupOverlay.current, pointerEvent.coordinate);
      setRulerWithPointerLength(Number(totalLength));
      rulerLength.current = updatedLength;
    }
    lastInnerPointWithLabel.current = innerPoint;
  }, [scaleLength, scaleLabel, themeType]);

  useEffect(() => {
    if (!pointerEvent || !clickedPoints.length || didUserDoubleClicked.current)
      return;
    const length = displayPointerLineAndGetTotalLength(rulerLength.current);
    setRulerWithPointerLength(Number(length));
    showPopup(popupOverlay.current, pointerEvent.coordinate);
  }, [pointerEvent]);

  useEffect(() => {
    if (clickedPoints.length < 2) return;
    const clearPointer = () => {
      removeLayerFeature(pointsLayer, RULER_FEATURES_IDS.POINTER_LINE);
      removeLayerFeature(
        pointsLayer,
        RULER_FEATURES_IDS.HOVER_RULER_MULTI_POINTS
      );
      if (pointsLayer.current.getSource()) {
        clearLabelPointsOnLine(pointsLayer);
      }
      addFinishingPointToLayer();
    };
    clearPointer();
    popupOverlay.current.setPosition(undefined);
    if (popup?.current) popup.current.blur();
    setRulerWithPointerLength(0);
    didUserDoubleClicked.current = true;
  }, [rightClickEvent]);

  return (
    <>
      {popup?.current ? (
        <div>
          <RulerPopup
            distance={rulerWithPointerLength}
            ref={popup}
            unit={scaleLabel}
          />
        </div>
      ) : null}
    </>
  );
};

Ruler.propTypes = {
  map: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  map: state.map,
});

export default connect(mapStateToProps)(Ruler);
